import React, { useLayoutEffect } from 'react';
import {
  FileCardsGrid,
  RequestAttentionRequestedAlert,
  RequestExtrasCard,
  RequestExtrasCardContent,
  RequestMediaSourceCard,
  RequestMediaSourceCardContent,
  RequestMultipleOutcomeCard,
  RequestMultipleOutcomeCardContent,
  RequestPageContainer,
  RequestPageContentMain,
} from 'src/pages/request/request-page/request-default-page';
import { progressInStandardTime } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { CirclePlus, FolderOpen, GitFork } from 'lucide-react';
import { CollapsibleCard, CollapsibleCardContent } from 'src/components/ui/collapsible-card';
import { RequestOutcomesSummaryCard } from 'src/features/requests/request-outcome/request-outcomes-summary-card';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { If } from 'src/components/If';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/components/ui/use-toast';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { RequestStatusCell } from 'src/features/requests/request-table/request-status-cell';
import { requestOutcomeStatus } from 'src/lib/services/api/request-api';
import { DecoratedRequestOutcomeContextProvider } from 'src/features/requests/request-outcome/use-decorated-request-outcome-context';
import { RequestOutcomesQueryContextProvider } from 'src/features/requests/request-outcome/use-request-outcomes-query-context';
import { CreateOutcomeFormDialog } from 'src/features/requests/request-outcome/create-outcome-form-dialog';
import { useRequestQueryMutationDecorator } from 'src/features/requests/use-request-query-mutation-decorator';
import { useRequestOutcomesQueryMutationDecorator } from 'src/features/requests/request-outcome/use-request-outcomes-query-mutation-decorator';
import { generatePath, useNavigate } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import {
  RequestDeliverableCardBadge,
  RequestDeliverableOutcomeCard,
  RequestDeliverableOutcomeCardBackgroundContent,
  RequestDeliverableOutcomeCardBackgroundContentFallback,
  RequestDeliverableOutcomeCardContent,
  RequestDeliverableOutcomeCardFooter,
  RequestDeliverableOutcomeCardForegroundContent,
  RequestDeliverableOutcomeCardHeader,
  RequestDeliverableOutcomeCardTitle,
} from 'src/features/requests/request-outcome/request-deliverable-outcome-card';
import { RequestDeliverableQueuedOutcomeCard } from 'src/features/requests/request-outcome/request-deliverable-queued-outcome-card';
import { RequestOutcomeAttentionButton } from 'src/features/requests/request-outcome/request-outcome-attention-button';
import { SpaceErrorFallback } from 'src/features/fallback';
import {
  deliverableOutcomesFilters,
  pendingOutcomesFilters,
} from 'src/features/requests/request-outcome/filter-queries';
import {
  EmptySpaceFallback,
  EmptySpaceFallbackDescription,
  EmptySpaceFallbackHeader,
  EmptySpaceFallbackImage,
  EmptySpaceFallbackTitle,
} from 'src/features/fallback/ui';
import { RequestOutcomeCreatedByIcon } from 'src/features/requests/request-outcome/pending-outcomes';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { useToggle } from 'src/lib/state-utils';
import { requestContentTab } from './request-page.utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { Label } from 'src/components/ui/label';
import dompurify from 'dompurify';
import { Str } from 'src/helpers';

// Multi-outcome parents have no project-level type/length/priority/etc — those
// live per-outcome — so the Summary tab just surfaces the project name here
// and defers the per-outcome detail to RequestOutcomesSummaryCard below. The
// client-provided brief is still project-level though, so it's shown here too.
const ProjectTitleCard: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useToggle(true);
  const request = useDecoratedRequestContext();

  return (
    <CollapsibleCard
      icon={<FolderOpen />}
      title={t('common:project_title', { defaultValue: 'Project title' })!}
      open={open}
      onOpenChange={setOpen}
      variant={'shadow'}
    >
      <CollapsibleCardContent className={'tw-flex tw-flex-col tw-gap-4'}>
        <p className={'tw-break-words tw-text-base tw-font-medium tw-text-text-strong'}>
          {request.title}
        </p>

        <div>
          <Label size={'lg'} className={'tw-mb-2'}>
            {t('common:description', { defaultValue: 'Description' })}
          </Label>
          <p
            className={'tw-whitespace-pre-line tw-break-words'}
            dangerouslySetInnerHTML={{
              __html: Str.wrapHrefs(
                dompurify.sanitize(
                  request.description?.video_request_description ??
                    t('common:unset', { defaultValue: '-' })!,
                ),
              ),
            }}
          />
        </div>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};

const RequestMultiOutcomeSkeleton: React.FC<React.ComponentProps<typeof Skeleton>> = ({
  className,
  ...props
}) => {
  return (
    <RequestPageContainer {...props} className={className}>
      <Skeleton className="tw-h-10 tw-w-1/3 tw-rounded-md" />
      <RequestPageContentMain className={'tw-grid-cols-12'}>
        <Skeleton className="tw-col-span-12 tw-h-full md:tw-col-span-8 lg:tw-col-span-9" />
        <Skeleton className="tw-col-span-12 tw-h-full md:tw-col-span-4 lg:tw-col-span-3" />
      </RequestPageContentMain>
    </RequestPageContainer>
  );
};

const DeliverablesTabHeader: React.FC = () => {
  const { t } = useTranslation();
  const request = useDecoratedRequestContext();
  const requestActions = useRequestQueryMutationDecorator(request.id);
  const { toast } = useToast();
  const [open, setOpen] = useToggle();

  const pendingOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    pendingOutcomesFilters,
  );
  const deliverableOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    deliverableOutcomesFilters,
  );

  const initialValues = {
    type: '',
    title: t('models/request:outcome.placeholder', {
      defaultValue: 'Outcome #:num',
      replace: { num: (request.raw.outcomes_count ?? 0) + 1 },
    }),
    total_length: undefined,
  };

  return (
    <div className={'tw-flex tw-items-center tw-justify-end tw-pb-2'}>
      <CreateOutcomeFormDialog
        initialValues={initialValues}
        open={open}
        onOpenChange={setOpen}
        onSubmit={async (values) => {
          await requestActions.createOutcome(values).then((outcome) => {
            toast({
              description: t('alerts:success.request_outcome_created', {
                defaultValue: ':type request outcome created',
                replace: {
                  type: t(`models/request:type.${outcome.type}`, {
                    defaultValue: t('common:unknown', { defaultValue: 'Unknown' }),
                  }),
                },
              }),
            });

            pendingOutcomesActions.push(outcome);
            pendingOutcomesActions.invalidate();
            deliverableOutcomesActions.invalidate();

            setOpen(false);
          });
        }}
      >
        <Button
          variant={'outline'}
          size={'iconStartSm'}
          type={'button'}
          disabled={!request.canAddOutcome}
        >
          <CirclePlus className={'tw-size-4'} />
          {t('actions:add_new_outcome', { defaultValue: 'Add new outcome' })}
        </Button>
      </CreateOutcomeFormDialog>
    </div>
  );
};

const MainContentTabsList: React.FC<
  React.ComponentProps<typeof TabsList> & {
    setActiveTab: React.Dispatch<React.SetStateAction<string>>;
    activeTab: string;
  }
> = ({ setActiveTab, activeTab, ...props }) => {
  const { t } = useTranslation();

  useLayoutEffect(() => {
    // Legacy `?tab=pending_outcomes` URLs land users on Deliverables now that
    // pending outcomes render as queued cards inside that grid.
    if (activeTab === requestContentTab.pendingOutcomes) {
      setActiveTab(requestContentTab.deliverables);
    }
  }, [activeTab, setActiveTab]);

  return (
    <TabsList variant={'start'} {...props}>
      <TabsTrigger
        padding={'unset'}
        className={'tw-rounded-lg tw-px-4 tw-py-3'}
        value={requestContentTab.summary}
      >
        {t(`common:${requestContentTab.summary}`, {
          defaultValue: 'Summary',
        })}
      </TabsTrigger>

      <TabsTrigger
        className={'tw-rounded-lg tw-px-4 tw-py-3'}
        value={requestContentTab.deliverables}
      >
        {t(`common:${requestContentTab.deliverables}`, {
          defaultValue: 'Deliverables',
        })}
      </TabsTrigger>
    </TabsList>
  );
};

const RequestMultiOutcomePageMainContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => {
  const [activeTab, setActiveTab] = useQueryParam<string>(
    'tab',
    withDefault(StringParam, requestContentTab.summary),
  );
  const request = useDecoratedRequestContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div ref={ref} {...props}>
      <Tabs
        onValueChange={(value) => setActiveTab(value)}
        defaultValue={activeTab}
        value={activeTab}
        className={'tw-flex tw-h-full tw-flex-col tw-gap-2'}
      >
        <MainContentTabsList
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          className={
            'tw-justify-start tw-gap-2 tw-overflow-x-auto tw-overflow-y-clip tw-border-0 tw-bg-transparent'
          }
        />

        <TabsContent className={'tw-relative tw-mt-0 tw-grow'} value={requestContentTab.summary}>
          <div className={'tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-4'}>
            <RequestAttentionRequestedAlert />
            <ScrollArea>
              <div className={'tw-flex tw-flex-col tw-gap-4 tw-px-1 tw-pb-1'}>
                <ProjectTitleCard />

                <RequestExtrasCard>
                  <RequestExtrasCardContent />
                </RequestExtrasCard>

                {/* Project strategy indicator ("Customized" / "Editor's
                    selection") — mirrors production, which shows it right below
                    the Details/Examples card. The dev refactor dropped this
                    mount, so the editor couldn't tell the strategy on sandbox. */}
                <RequestMultipleOutcomeCard>
                  <RequestMultipleOutcomeCardContent />
                </RequestMultipleOutcomeCard>

                <RequestMediaSourceCard>
                  <RequestMediaSourceCardContent>
                    <If when={request.hasMedia} else={t('common:unset', { defaultValue: '-' })}>
                      <FileCardsGrid
                        className={
                          'tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 desktop:tw-grid-cols-4'
                        }
                        files={request.media}
                        requestId={request.id}
                      />
                    </If>
                  </RequestMediaSourceCardContent>
                </RequestMediaSourceCard>

                <RequestOutcomesSummaryCard />
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent className={'tw-relative tw-grow'} value={requestContentTab.deliverables}>
          <div
            className={
              'tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-2 tw-overflow-auto tw-rounded-lg tw-border tw-bg-background tw-p-4'
            }
          >
            <DeliverablesTabHeader />

            <QueryErrorResetBoundary>
              {({ reset }) => (
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }: any) => (
                    <SpaceErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
                  )}
                  onReset={reset}
                >
                  <React.Suspense fallback={<Skeleton className={'tw-w-full tw-grow'} />}>
                    <RequestOutcomesQueryContextProvider
                      id={request.id}
                      filters={deliverableOutcomesFilters}
                    >
                      {({ data }) => (
                        <If
                          when={!!data?.items.length}
                          else={
                            <EmptySpaceFallback>
                              <EmptySpaceFallbackImage className={'tw-mx-auto'} />

                              <EmptySpaceFallbackHeader className={'tw-gap-2 tw-text-center'}>
                                <EmptySpaceFallbackTitle>
                                  {t('common:empty_space', {
                                    defaultValue: "There's an empty space",
                                  })}
                                </EmptySpaceFallbackTitle>
                                <EmptySpaceFallbackDescription>
                                  {t('Create first contact to make a deliverable')}
                                </EmptySpaceFallbackDescription>
                              </EmptySpaceFallbackHeader>
                            </EmptySpaceFallback>
                          }
                        >
                          <div
                            className={
                              'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3 desktop:tw-grid-cols-4'
                            }
                          >
                            {data?.items.map((outcome, i) => (
                              <DecoratedRequestOutcomeContextProvider outcome={outcome} key={i}>
                                {(outcome) =>
                                  // #2532: an outcome mid re-edit is technically
                                  // Queued but already carries a delivered
                                  // revision -- only route genuinely empty
                                  // (never-delivered) outcomes to the
                                  // non-interactive Queued card, so re-edit
                                  // outcomes stay clickable and show their
                                  // actual video instead of the generic
                                  // placeholder.
                                  outcome.raw.status === requestOutcomeStatus.queued &&
                                  !outcome.hasRevisions ? (
                                    <RequestDeliverableQueuedOutcomeCard />
                                  ) : (
                                  <RequestDeliverableOutcomeCard>
                                    <RequestDeliverableOutcomeCardContent
                                      onClick={() =>
                                        navigate(
                                          generatePath(appRoutes.requestRevisions, {
                                            requestId: outcome.id,
                                            companyId: outcome.raw.company_id,
                                          }),
                                        )
                                      }
                                    >
                                      <RequestDeliverableOutcomeCardBackgroundContent>
                                        <If
                                          when={!!outcome.revisionPreviewImage}
                                          else={
                                            <RequestDeliverableOutcomeCardBackgroundContentFallback />
                                          }
                                        >
                                          <img
                                            className={
                                              'tw-h-full tw-w-full tw-rounded-t-lg tw-object-cover'
                                            }
                                            src={outcome.revisionPreviewImage}
                                            alt={outcome.raw.title}
                                          />
                                        </If>
                                      </RequestDeliverableOutcomeCardBackgroundContent>

                                      <RequestDeliverableOutcomeCardForegroundContent>
                                        <div
                                          className={
                                            'tw-flex tw-w-full tw-flex-wrap tw-items-start tw-justify-end tw-gap-2'
                                          }
                                        >
                                          {/* Card content has its own onClick that navigates to
                                              the outcome's revisions page — stop propagation here
                                              so opening the attention modal doesn't also navigate. */}
                                          <div
                                            className={
                                              'tw-ms-auto tw-flex tw-items-center tw-gap-2'
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <RequestOutcomeAttentionButton />
                                            {/* #2542: one resolved pill instead of a separate
                                                "Needs re-edit" badge stacked beside the raw status.
                                                needsReedit (= requested_reedit_at) stays set through
                                                Editing → Ready for Review, so the old standalone
                                                badge lingered next to the advancing status.
                                                RequestStatusCell collapses it via
                                                resolveDisplayedRequestStatus (the single-pill
                                                approach the queued card adopted in #2510) and adds
                                                the trailing re-edit spinner that card lacks. */}
                                            <RequestStatusCell request={outcome.raw} size={'default'} />
                                          </div>
                                        </div>

                                        <If when={!!outcome.revisionPreview?.durationInSeconds}>
                                          <RequestDeliverableCardBadge
                                            className={
                                              'tw-ms-auto tw-mt-auto tw-bg-background tw-py-1'
                                            }
                                          >
                                            {progressInStandardTime(
                                              outcome.revisionPreview?.durationInSeconds ?? 0,
                                            )}
                                          </RequestDeliverableCardBadge>
                                        </If>
                                      </RequestDeliverableOutcomeCardForegroundContent>
                                    </RequestDeliverableOutcomeCardContent>

                                    <RequestDeliverableOutcomeCardHeader>
                                      <RequestDeliverableOutcomeCardTitle>
                                        {outcome.raw.title}
                                      </RequestDeliverableOutcomeCardTitle>
                                    </RequestDeliverableOutcomeCardHeader>

                                    <RequestDeliverableOutcomeCardFooter>
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div>
                                              <RequestDeliverableCardBadge>
                                                <GitFork size={16} className={'tw-me-2'} />
                                                <p>v{outcome.raw.versions_count ?? 0}</p>
                                              </RequestDeliverableCardBadge>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {t('common:version', {
                                              defaultValue: 'Version',
                                            })}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>

                                      <RequestDeliverableCardBadge>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div>
                                                <RequestOutcomeCreatedByIcon
                                                  size={16}
                                                  createdBy={outcome.raw.created_by}
                                                />
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              {t(
                                                `models/request:outcome.created_by.${outcome.raw.created_by}`,
                                                {
                                                  defaultValue: 'Created by unknown',
                                                },
                                              )}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </RequestDeliverableCardBadge>
                                    </RequestDeliverableOutcomeCardFooter>
                                  </RequestDeliverableOutcomeCard>
                                  )
                                }
                              </DecoratedRequestOutcomeContextProvider>
                            ))}
                          </div>
                        </If>
                      )}
                    </RequestOutcomesQueryContextProvider>
                  </React.Suspense>
                </ErrorBoundary>
              )}
            </QueryErrorResetBoundary>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
});
RequestMultiOutcomePageMainContent.displayName = 'RequestDefaultPageMainContent';

export { RequestMultiOutcomeSkeleton, RequestMultiOutcomePageMainContent };
